import { useMutation, useQuery } from '@tanstack/react-query';
import { raynetClient } from '@/api/raynetClient';
import { offlineQueue } from '@/api/offlineQueue';
import { FormSubmissionData, RaynetLead, RaynetPerson, DealerSurveyData, SurveySubmissionRequest } from '@/api/types';

export function useRaynetConnection() {
  return useQuery({
    queryKey: ['raynet-connection'],
    queryFn: () => raynetClient.testConnection(),
    refetchInterval: 30000, // Test každých 30 sekund
    retry: false,
  });
}

export function useContactSources() {
  return useQuery({
    queryKey: ['raynet-contact-sources'],
    queryFn: () => raynetClient.getContactSources(),
    enabled: raynetClient.isConfigured(),
    staleTime: 10 * 60 * 1000, // 10 minut
    retry: 1,
  });
}

export function useLeadCategories() {
  return useQuery({
    queryKey: ['raynet-lead-categories'],
    queryFn: () => raynetClient.getLeadCategories(),
    enabled: raynetClient.isConfigured(),
    staleTime: 10 * 60 * 1000, // 10 minut
    retry: 1,
  });
}

export function useSubmitForm() {
  return useMutation({
    mutationFn: async (data: FormSubmissionData) => {
      try {
        // Pokusíme se odeslat přímo do Raynet
        if (!raynetClient.isConfigured()) {
          throw new Error('Raynet není nakonfigurován');
        }

        // Vytvoříme osobu
        const personData: RaynetPerson = {
          firstName: data.firstName,
          lastName: data.lastName,
          primaryEmail: data.email,
          emails: data.email ? [{ email: data.email, primary: true }] : [],
          telephones: data.phone ? [{ telephone: data.phone, primary: true }] : [],
          addresses: (data.street || data.city) ? [{
            address: {
              street: data.street,
              city: data.city,
              zipCode: data.postalCode,
            },
            primary: true,
          }] : [],
        };

        const personResult = await raynetClient.createPerson(personData);
        
        if (!personResult.success || !personResult.data) {
          throw new Error('Chyba při vytváření osoby');
        }

        // Vytvoříme lead
        const leadData: RaynetLead = {
          name: `${data.firstName} ${data.lastName} - ${data.stoveType || 'Kamna'}`,
          firstName: data.firstName,
          lastName: data.lastName,
          personId: personResult.data.id,
          topic: data.stoveType || 'Zájem o kamna',
          description: [
            data.message,
            data.heatingArea ? `Plocha vytápění: ${data.heatingArea}` : '',
            data.budget ? `Rozpočet: ${data.budget}` : '',
            data.installation ? `Instalace: ${data.installation}` : '',
          ].filter(Boolean).join('\n'),
        };

        const leadResult = await raynetClient.createLead(leadData);
        
        if (!leadResult.success) {
          throw new Error('Chyba při vytváření lead');
        }

        console.log('Formulář úspěšně odeslán do Raynet');
        return { success: true, personId: personResult.data.id, leadId: leadResult.data?.id };

      } catch (error) {
        console.warn('Raynet API nedostupné, ukládám do offline queue:', error);
        
        // Uložíme do offline queue
        const queueId = await offlineQueue.addSubmissionToQueue(data);
        
        return { 
          success: true, 
          offline: true, 
          queueId,
          message: 'Formulář byl uložen a bude odeslán při obnovení připojení k API'
        };
      }
    },
  });
}

export function useOfflineQueue() {
  return useQuery({
    queryKey: ['offline-queue'],
    queryFn: () => ({
      queue: offlineQueue.getQueue(),
      pendingCount: offlineQueue.getPendingCount(),
    }),
    refetchInterval: 5000, // Refresh každých 5 sekund
  });
}

// Nový hook pro odesílání dotazníku prodejce
export function useSubmitSurvey() {
  return useMutation({
    mutationFn: async (data: { surveyData: DealerSurveyData; representative: string }) => {
      try {
        // Připravíme request pro backend
        const surveyRequest: SurveySubmissionRequest = {
          surveyData: data.surveyData,
          representative: data.representative,
          submissionDate: new Date().toISOString()
        };

        console.log('🚀 Odesílání dotazníku do Raynetu:', surveyRequest);

        // Odešleme pomocí nové submitSurvey metody
        const result = await raynetClient.submitSurvey(surveyRequest);
        
        if (!result.success) {
          throw new Error(result.message || 'Chyba při odesílání dotazníku');
        }

        console.log('✅ Dotazník úspěšně odeslán:', result);
        return result;

      } catch (error) {
        console.error('❌ Chyba při odesílání dotazníku:', error);
        
        // V případě chyby můžeme implementovat offline queue pro dotazníky
        // TODO: Implementovat offline queue pro dotazníky v budoucnu
        
        throw error;
      }
    },
  });
} 